import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {Button} from 'primereact/button'
import {Checkbox} from 'primereact/checkbox'
import {Sidebar} from 'primereact/sidebar'
import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
} from 'react'
import {ReactComponent as DuoColumn} from '../../assets/icons/DuoColumn.svg'
import TranslateUtils from '../../i18n/TranslateUtils'
import {isArrayAndNotEmpty} from '../observations/Utils'
import {
    columnEventTypes,
    onChangeColumnsViewedListener,
} from './broadcast/ColumnEvent'
import {
    fileEvent,
    fileEventTypes,
    getFileEventKey,
    onReceiveExportColumnsDataListener,
    removeFileEventListener,
} from './broadcast/FileEvent'
import {exportColumnTypes} from './Constant'
import {useDatatableContext} from './DataTableContext'
import {
    columnsVisibilityTypes,
    getColumnVisibilityByTableType,
    getShouldRememberColumns,
    storeShouldRememberColumns,
} from './StyledTableUtils'
import './styles.css'

const selectedType = {
    shown: 'shown',
    hidden: 'hidden',
}

const StyledViewedColumns = forwardRef((props, ref) => {
    useSignals()

    useImperativeHandle(ref, () => ({
        showForm: handleShowForm,
        hideForm: handleHideForm,
    }))

    const {translateDatatable, translateKeys} = TranslateUtils()

    const {tableType, columnList} = useDatatableContext()

    const visible = useSignal(false)
    const shownColumns = useSignal([])
    const hiddenColumns = useSignal([])
    const shouldRememberColumns = useSignal(false)

    const endShownRegionRef = useRef(null)
    const endHiddenRegionRef = useRef(null)

    const handleShowForm = () => {
        visible.value = true

        if (
            shownColumns.value.length === 0 &&
            hiddenColumns.value.length === 0
        ) {
            let storedShownColumns = []
            let storedHiddenColumns = []

            const didRememberSettings = getShouldRememberColumns(tableType)
            if (!!didRememberSettings) {
                storedShownColumns = getColumnVisibilityByTableType(
                    tableType,
                    columnsVisibilityTypes.shown,
                )
                storedHiddenColumns = getColumnVisibilityByTableType(
                    tableType,
                    columnsVisibilityTypes.hidden,
                )
            }
            shownColumns.value =
                didRememberSettings || isArrayAndNotEmpty(storedShownColumns)
                    ? [...storedShownColumns]
                    : isArrayAndNotEmpty(columnList)
                    ? [...columnList?.filter(x => x.initialShown)]
                    : []

            hiddenColumns.value =
                didRememberSettings || isArrayAndNotEmpty(storedHiddenColumns)
                    ? [...storedHiddenColumns]
                    : isArrayAndNotEmpty(columnList)
                    ? [...columnList?.filter(x => !x.initialShown)]
                    : []

            shouldRememberColumns.value = didRememberSettings
        }
    }

    const handleHideForm = () => {
        visible.value = false
    }

    const handleOnSelected = (type, item) => {
        if (type === selectedType.hidden) {
            if (shownColumns.value.length > 1) {
                let newShownColumn = shownColumns.value.filter(
                    x => x.field !== item.field,
                )
                shownColumns.value = newShownColumn
                let newHiddenColumn = [
                    ...hiddenColumns.value,
                    {...item, initialShown: false},
                ]
                hiddenColumns.value = newHiddenColumn
                if (endShownRegionRef?.current) {
                    endShownRegionRef.current.scrollIntoView({
                        behavior: 'smooth',
                    })
                }
            }
        }

        if (type === selectedType.shown) {
            if (hiddenColumns.value.length > 0) {
                let newHiddenColumn = hiddenColumns.value.filter(
                    x => x.field !== item.field,
                )
                hiddenColumns.value = newHiddenColumn

                let newShownColumn = [
                    ...shownColumns.value,
                    {...item, initialShown: true},
                ]
                shownColumns.value = newShownColumn

                if (endShownRegionRef?.current) {
                    endShownRegionRef.current.scrollIntoView({
                        behavior: 'smooth',
                    })
                }
            }
        }
    }

    const handleChangeColumnsViewed = () => {
        onChangeColumnsViewedListener(
            tableType,
            columnEventTypes.COLUMN_VIEWED_CHANGE,
            shownColumns.value,
            hiddenColumns.value,
        )
        handleHideForm()
    }

    const handleRestoreColumnsViewed = () => {
        onChangeColumnsViewedListener(
            tableType,
            columnEventTypes.COLUMN_VIEWED_RESTORE_DEFAULT,
        )

        shownColumns.value = isArrayAndNotEmpty(columnList)
            ? [...columnList?.filter(x => x.initialShown)]
            : []
        hiddenColumns.value = isArrayAndNotEmpty(columnList)
            ? [...columnList?.filter(x => !x.initialShown)]
            : []
        shouldRememberColumns.value = false
    }

    const handleCheckShouldRememberColumns = value => {
        shouldRememberColumns.value = value

        storeShouldRememberColumns(tableType, value)
    }

    const handleSendColumnsForExport = useCallback(
        exportColumnType => {
            if (exportColumnType === exportColumnTypes.inViewColumns) {
                onReceiveExportColumnsDataListener(
                    tableType,
                    isArrayAndNotEmpty(shownColumns.value)
                        ? [...shownColumns.value]
                        : isArrayAndNotEmpty(columnList)
                        ? [...columnList?.filter(x => x.initialShown)]
                        : [],
                )
            } else if (exportColumnType === exportColumnTypes.allColumns) {
                onReceiveExportColumnsDataListener(tableType, columnList)
            }
        },
        [columnList, shownColumns.value, tableType],
    )

    useEffect(() => {
        fileEvent.on(
            `${getFileEventKey(
                tableType,
                fileEventTypes.GET_EXPORT_COLUMNS_DATA,
            )}`,
            handleSendColumnsForExport,
        )

        return () => {
            removeFileEventListener(
                tableType,
                fileEventTypes.GET_EXPORT_COLUMNS_DATA,
            )
        }
    }, [tableType])

    return (
        <Sidebar
            className="w-full md:w-25rem lg:w-30rem"
            visible={visible.value}
            onHide={handleHideForm}
            showCloseIcon={true}
            blockScroll
            position="right"
            baseZIndex={9999}>
            <div className="py-2 px-1">
                {/* Header */}
                <div className="flex align-items-center">
                    <div className="mr-2">
                        <DuoColumn style={{fontSize: '30px'}} />
                    </div>
                    <div>
                        <h3 className="font-bold m-0">
                            {translateDatatable('Edit Column Visibilities')}
                        </h3>
                    </div>
                </div>
                <div>
                    <p>
                        {translateDatatable(
                            'Customizing your table view by choosing which columns to display',
                        )}
                    </p>
                </div>
                <hr />
                {/* Content */}
                <div className="mb-5">
                    <div className="mb-3">
                        <div className="flex align-items-center justify-content-between mb-3">
                            <div>
                                <h5 className="mb-0 font-bold">
                                    <span className="mr-2">
                                        <i className="pi pi-eye"></i>
                                    </span>
                                    {translateDatatable('Shown Columns')}
                                </h5>
                            </div>
                            <div>
                                <h5 className="mb-0  font-bold">
                                    {shownColumns.value?.length > 0
                                        ? `(+${shownColumns.value.length})`
                                        : ''}
                                </h5>
                            </div>
                        </div>
                        <div
                            className="flex flex-column gap-1 bg-gray-100 border-round-lg p-2 overflow-scroll"
                            style={{maxHeight: '35vh'}}>
                            <div className="h-full pr-1 show-scrollbar">
                                {shownColumns.value?.length > 0 ? (
                                    shownColumns.value?.map((item, idx) => (
                                        <Button
                                            key={`shown-${item.field}-${idx}`}
                                            className="col-12 p-button-md mb-1 text-left"
                                            icon="pi pi-eye"
                                            label={translateKeys(item.header)}
                                            onClick={() =>
                                                handleOnSelected(
                                                    selectedType.hidden,
                                                    item,
                                                )
                                            }
                                        />
                                    ))
                                ) : (
                                    <p>
                                        {translateDatatable(
                                            'No available shown columns',
                                        )}
                                    </p>
                                )}
                                <div ref={endShownRegionRef}></div>
                            </div>
                        </div>
                    </div>
                    <div className="mb-3">
                        <div className="flex align-items-center justify-content-between mb-3">
                            <div>
                                <h5 className="mb-0  font-bold">
                                    <span className="mr-2">
                                        <i className="pi pi-eye-slash"></i>
                                    </span>
                                    {translateDatatable('Hidden Columns')}
                                </h5>
                            </div>
                            <div>
                                <h5 className="mb-0  font-bold">
                                    {hiddenColumns.value?.length > 0
                                        ? `(-${hiddenColumns.value.length})`
                                        : ''}
                                </h5>
                            </div>
                        </div>

                        <div
                            className="flex flex-column gap-1 bg-gray-100 border-round-lg p-2 overflow-scroll"
                            style={{maxHeight: '35vh'}}>
                            <div className="h-full pr-1 show-scrollbar">
                                {hiddenColumns.value?.length > 0 ? (
                                    hiddenColumns.value?.map((item, idx) => (
                                        <Button
                                            key={`hidden-${item.field}-${idx}`}
                                            className="col-12 p-button-md mb-1 text-left hover-bg-change"
                                            icon="pi pi-eye-slash"
                                            label={translateKeys(item.header)}
                                            onClick={() =>
                                                handleOnSelected(
                                                    selectedType.shown,
                                                    item,
                                                )
                                            }
                                        />
                                    ))
                                ) : (
                                    <p className="mb-0">
                                        {translateDatatable(
                                            'No available hidden columns',
                                        )}
                                    </p>
                                )}
                                <div ref={endHiddenRegionRef}></div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Footer */}
                <div className="mb-5 pb-5">
                    <div className="flex align-items-center mb-3">
                        <div className="mr-2">
                            <Checkbox
                                checked={shouldRememberColumns.value}
                                onChange={e => {
                                    handleCheckShouldRememberColumns(e.checked)
                                }}
                            />
                        </div>
                        <div>
                            <span>
                                {translateDatatable(
                                    'Remember my column view settings',
                                )}
                            </span>
                        </div>
                    </div>
                    <Button
                        label={translateDatatable(
                            'RESTORE TO DEFAULT',
                        ).toUpperCase()}
                        icon="pi pi-refresh"
                        className="col-12 p-button-lg p-button-outlined font-bold mb-2"
                        onClick={() => {
                            handleRestoreColumnsViewed()
                        }}
                    />
                    <Button
                        label={translateDatatable('Submit').toUpperCase()}
                        icon="pi pi-send"
                        className="col-12 p-button-lg font-bold"
                        onClick={() => {
                            handleChangeColumnsViewed()
                        }}
                    />
                </div>
            </div>
        </Sidebar>
    )
})

export default StyledViewedColumns
